import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader, CButton } from '../../components/common';
import { IoStar, IoStarOutline } from 'react-icons/io5';
import { submitExpertRating } from '../../api';

const RateExpert: React.FC = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const expertName = searchParams.get('expert') || 'Expert';
  const expertId = searchParams.get('expertId');

  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  const handleSubmit = async () => {
    if (rating === 0 || !bookingId || !expertId) return;

    setLoading(true);
    try {
      await submitExpertRating({
        id: parseInt(expertId),
        rating,
        feedback,
        booking_id: parseInt(bookingId),
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting rating:', error);
      alert('Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/experts');
  };

  if (submitted) {
    return (
      <div
        className="p-4 min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: colors.green + '20' }}
        >
          <CText type="B32" color={colors.green}>✓</CText>
        </div>

        <CText type="B20" color={colors.textColor} style={{ marginBottom: 8, textAlign: 'center' }}>
          Thank You!
        </CText>

        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24, textAlign: 'center' }}>
          Your feedback helps us improve our service and helps experts provide better consultations.
        </CText>

        <CButton title="Back to Experts" onPress={() => navigate('/experts')} />
      </div>
    );
  }

  return (
    <div
      className="p-4 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {loading && <CLoader />}

      <CHeader title="Rate Your Session" />

      {/* Expert Card */}
      <div
        className="rounded-xl p-4 mb-6 flex items-center gap-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold"
          style={{ backgroundColor: colors.primary, color: colors.white }}
        >
          {expertName.charAt(0)}
        </div>
        <div>
          <CText type="B16" color={colors.textColor}>
            {expertName}
          </CText>
          <CText type="r12" color={colors.grayScale500}>
            Session completed
          </CText>
        </div>
      </div>

      {/* Rating Section */}
      <div className="text-center mb-8">
        <CText type="B18" color={colors.textColor} style={{ marginBottom: 4 }}>
          How was your experience?
        </CText>
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 16 }}>
          Rate the quality of your consultation
        </CText>

        {/* Stars */}
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              className="p-1 transition-transform hover:scale-110"
            >
              {React.createElement(
                star <= rating ? IoStar : IoStarOutline as any,
                {
                  size: 40,
                  color: star <= rating ? '#FFD700' : colors.grayScale500,
                }
              )}
            </button>
          ))}
        </div>

        {/* Rating Label */}
        {rating > 0 && (
          <CText type="M16" color={colors.primary}>
            {ratingLabels[rating]}
          </CText>
        )}
      </div>

      {/* Feedback Input */}
      <div className="mb-6">
        <CText type="M14" color={colors.textColor} style={{ marginBottom: 8 }}>
          Share your feedback (optional)
        </CText>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Tell us about your experience..."
          className="w-full p-4 rounded-xl outline-none resize-none"
          style={{
            backgroundColor: colors.inputBackground,
            color: colors.textColor,
            minHeight: 120,
            border: `1px solid ${colors.borderColor}`,
          }}
        />
      </div>

      {/* Quick Feedback Tags */}
      <div className="mb-8">
        <CText type="r12" color={colors.grayScale500} style={{ marginBottom: 8 }}>
          Quick feedback
        </CText>
        <div className="flex flex-wrap gap-2">
          {['Knowledgeable', 'Professional', 'Patient', 'Clear explanations', 'Helpful advice', 'On time'].map((tag) => (
            <button
              key={tag}
              onClick={() => {
                if (feedback.includes(tag)) {
                  setFeedback(feedback.replace(tag + ', ', '').replace(', ' + tag, '').replace(tag, ''));
                } else {
                  setFeedback(feedback ? feedback + ', ' + tag : tag);
                }
              }}
              className="px-3 py-2 rounded-full transition-colors"
              style={{
                backgroundColor: feedback.includes(tag) ? colors.primary : colors.inputBackground,
                color: feedback.includes(tag) ? colors.white : colors.textColor,
                border: `1px solid ${feedback.includes(tag) ? colors.primary : colors.borderColor}`,
              }}
            >
              <CText type="r12" color={feedback.includes(tag) ? colors.white : colors.textColor}>
                {tag}
              </CText>
            </button>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <CButton
        title={rating === 0 ? 'Select a Rating' : 'Submit Review'}
        onPress={handleSubmit}
        disabled={rating === 0}
      />

      {/* Skip Button */}
      <button
        onClick={handleSkip}
        className="w-full mt-4 py-3"
      >
        <CText type="M14" color={colors.grayScale500}>
          Skip for now
        </CText>
      </button>
    </div>
  );
};

export default RateExpert;
